import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';

/**
 * Custom hook for listening to a single user item's progress
 * @param {string} userId - User ID
 * @param {string} itemId - Item ID to listen to
 * @param {'words'|'sentences'} contentType - Type of content
 * @returns {Object} { userProgress, loading, error }
 */
export const useItemListener = (userId, itemId, contentType) => {
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || !itemId || !contentType) {
      setUserProgress(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const collectionName = contentType === 'words' ? 'user_words' : 'user_sentences';
    const userProgressRef = doc(firestore, 'users', userId, collectionName, itemId);

    const unsubscribe = onSnapshot(
      userProgressRef,
      (doc) => {
        if (doc.exists()) {
          setUserProgress({ id: doc.id, ...doc.data() });
        } else {
          setUserProgress(null);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error(`Error listening to ${contentType} progress:`, err);
        setError(err);
        setUserProgress(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, itemId, contentType]);

  return { userProgress, loading, error };
};